import React, { useEffect, useRef } from "react";

const SpiderWebBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationIdRef = useRef<number | null>(null);

  useEffect(() => {
    const SpiderWebEffect = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const config = {
        particleCount: 150,
        maxDistance: 150,
        mouseRadius: 200,
        particleSpeed: 0.3,
        mouseAttraction: 0.03,
        lineOpacity: 0.4,
        particleOpacity: 0.9,
        particleSize: 1.5,
        lineWidth: 0.8,
        backgroundColor: "#000000",
        particleColor: "#ffffff",
        lineColor: "#ffffff",
      };

      let particles: any[] = [];
      let mouse = { x: 0, y: 0, isActive: false };

      function resizeCanvas() {
        if (!canvas) return;
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      }

      function createParticles() {
        if (!canvas) return;
        particles = [];
        const cols = Math.ceil(Math.sqrt(config.particleCount * 1.5));
        const rows = Math.ceil(config.particleCount / cols);
        const cellWidth = canvas.width / cols;
        const cellHeight = canvas.height / rows;

        for (let i = 0; i < config.particleCount; i++) {
          const col = i % cols;
          const row = Math.floor(i / cols);
          const baseX = (col + 0.5) * cellWidth;
          const baseY = (row + 0.5) * cellHeight;
          const randomOffsetX = (Math.random() - 0.5) * cellWidth * 0.8;
          const randomOffsetY = (Math.random() - 0.5) * cellHeight * 0.8;
          particles.push({
            x: baseX + randomOffsetX,
            y: baseY + randomOffsetY,
            vx: (Math.random() - 0.5) * config.particleSpeed,
            vy: (Math.random() - 0.5) * config.particleSpeed,
            originalX: baseX + randomOffsetX,
            originalY: baseY + randomOffsetY,
            size: Math.random() * config.particleSize + 0.5,
          });
        }
        // Añadir algunas partículas aleatorias extras
        for (let i = 0; i < 20; i++) {
          particles.push({
            x: Math.random() * canvas.width,
            y: Math.random() * canvas.height,
            vx: (Math.random() - 0.5) * config.particleSpeed,
            vy: (Math.random() - 0.5) * config.particleSpeed,
            originalX: Math.random() * canvas.width,
            originalY: Math.random() * canvas.height,
            size: Math.random() * config.particleSize + 0.5,
          });
        }
      }

      function updateParticles() {
        if (!canvas) return;
        particles.forEach((p: any) => {
          p.x += p.vx;
          p.y += p.vy;

          if (mouse.isActive) {
            const dx = mouse.x - p.x;
            const dy = mouse.y - p.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < config.mouseRadius) {
              const force =
                (config.mouseRadius - distance) / config.mouseRadius;
              const attraction = force * config.mouseAttraction;
              p.vx += dx * attraction * 0.01;
              p.vy += dy * attraction * 0.01;
            }
          }

          const returnForce = 0.002;
          p.vx += (p.originalX - p.x) * returnForce;
          p.vy += (p.originalY - p.y) * returnForce;
          p.vx *= 0.98;
          p.vy *= 0.98;

          if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
          if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

          p.x = Math.max(0, Math.min(canvas.width, p.x));
          p.y = Math.max(0, Math.min(canvas.height, p.y));
        });
      }

      function drawConnections() {
        if (!ctx) return;
        for (let i = 0; i < particles.length; i++) {
          for (let j = i + 1; j < particles.length; j++) {
            const p1 = particles[i];
            const p2 = particles[j];
            const dx = p2.x - p1.x;
            const dy = p2.y - p1.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < config.maxDistance) {
              const opacity =
                (1 - distance / config.maxDistance) * config.lineOpacity * 0.5;
              ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
              ctx.lineWidth =
                config.lineWidth * (1 - distance / config.maxDistance);
              ctx.beginPath();
              ctx.moveTo(p1.x, p1.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.stroke();
            }
          }
        }
      }

      function drawMouseConnections() {
        if (!ctx) return;
        if (!mouse.isActive) return;
        particles.forEach((p) => {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < config.mouseRadius) {
            const opacity = (1 - distance / config.mouseRadius) * 0.3;
            ctx.strokeStyle = `rgba(255, 255, 255, ${opacity})`;
            ctx.lineWidth = 1.5 * (1 - distance / config.mouseRadius);
            ctx.beginPath();
            ctx.moveTo(mouse.x, mouse.y);
            ctx.lineTo(p.x, p.y);
            ctx.stroke();
          }
        });
      }

      function drawParticles() {
        if (!ctx) return;
        particles.forEach((p) => {
          let opacity = config.particleOpacity * 0.6;
          if (mouse.isActive) {
            const dx = mouse.x - p.x;
            const dy = mouse.y - p.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            if (distance < config.mouseRadius) {
              opacity = Math.min(
                1,
                opacity + (1 - distance / config.mouseRadius) * 0.3
              );
            }
          }
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        });
      }

      function clearCanvas() {
        if (!ctx) return;
        if (!canvas) return;
        ctx.fillStyle = config.backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      function animate() {
        clearCanvas();
        updateParticles();
        drawConnections();
        drawMouseConnections();
        drawParticles();
        animationIdRef.current = requestAnimationFrame(animate);
      }

      function handleMouseMove(e: MouseEvent) {
        mouse.x = e.clientX;
        mouse.y = e.clientY;
        mouse.isActive = true;
      }

      function handleMouseLeave() {
        mouse.isActive = false;
      }

      function handleClick(e: MouseEvent) {
        particles.push({
          x: e.clientX,
          y: e.clientY,
          vx: (Math.random() - 0.5) * config.particleSpeed * 2,
          vy: (Math.random() - 0.5) * config.particleSpeed * 2,
          originalX: e.clientX,
          originalY: e.clientY,
          size: Math.random() * config.particleSize + 1,
        });

        if (particles.length > config.particleCount * 1.5) {
          particles.shift();
        }
      }

      function init() {
        resizeCanvas();
        createParticles();
        animate();
      }

      window.addEventListener("resize", resizeCanvas);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseleave", handleMouseLeave);
      window.addEventListener("click", handleClick);

      init();

      return () => {
        window.removeEventListener("resize", resizeCanvas);
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseleave", handleMouseLeave);
        window.removeEventListener("click", handleClick);
        if (animationIdRef.current)
          cancelAnimationFrame(animationIdRef.current);
      };
    };

    SpiderWebEffect();
  }, []);

  return <canvas ref={canvasRef} id="canvas" className="spiderweb-canvas" />;
};

export default SpiderWebBackground;
